'use client';
import React, {
  PropsWithChildren,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { UserProductComponentType } from '@osmosis/shared/types/user';
import { UserProps } from '@osmosis/shared/types/user';

import {
  hasPrime as userHasPrime,
  hasOnlyPaidPrime as userHasOnlyPaidPrime,
} from '../enums/primeStatus';
import { useQuery, useQueryClient } from 'react-query';
import { buildPlansUrl } from '../util/plansUrl';

export type UserContextProps = {
  user: UserProps | null;
  entitlements?: any[]; // TODO
  hasPrime: boolean;
  hasOnlyPaidPrime: boolean;
  hasFreeTrialPrime: boolean;
  hasExpiredPrime: boolean;
  userPlanCode: string;
  isUserLoaded: boolean;
  hasProductComponent: (name: UserProductComponentType) => boolean;
  setUser: (user: UserProps) => void;
  unsetUser: () => void;
  getPlansUrl: (query?: string) => string;
  hasEolsToken?: boolean;
};

const UserContext = React.createContext<UserContextProps>({
  user: null,
  hasPrime: false,
  hasOnlyPaidPrime: false,
  hasFreeTrialPrime: false,
  hasExpiredPrime: false,
  isUserLoaded: false,
  userPlanCode: '',
  hasProductComponent: () => false,
  setUser: () => {
    /* no-op */
  },
  unsetUser: () => {
    /* no-op */
  },
  getPlansUrl: () => '/plans',
  hasEolsToken: false,
});

/**
 * User provider.
 */
export const UserProvider = (
  props: PropsWithChildren<{
    user?: UserProps;
    loadUserOnClient?: boolean;
    isLoggedIn?: boolean;
    hasEolsToken?: boolean;
  }>,
) => {
  const {
    children,
    user: initialUser = null,
    loadUserOnClient,
    isLoggedIn,
    hasEolsToken,
  } = props;
  const [clientUser, setClientUser] = useState<UserProps | null>(initialUser);
  const queryClient = useQueryClient();
  const queryKey = ['user'];
  // otherwise fetch user from the API
  const { data: apiUser, isLoading: isApiUserLoading } =
    useQuery<UserProps | null>({
      enabled: !!loadUserOnClient && (!!isLoggedIn || !!initialUser),
      queryKey: queryKey,
      queryFn: async () => {
        const response = await fetch('/api/user');
        if (response.status === 403) return null;

        const { user } = await response.json();

        return user;
      },
    });
  // This is how we'll fetch entitlements on the client, but we don't need it enabled at this time
  // const { data: entitlements } = useQuery({
  //   queryKey: ['entitlements'],
  //   queryFn: async () => {
  //     const response = await fetch('/api/user/entitlements');
  //     const { entitlements } = await response.json();
  //     return entitlements;
  //   },
  // });

  const user = apiUser || clientUser;
  const isUserLoaded = !isApiUserLoading;

  const handleSetUser = async (user: UserProps) => {
    queryClient.setQueryData(['user'], user);

    return setClientUser(user);
  };

  const handleUnsetUser = () => {
    setClientUser(null);
  };

  const hasProductComponent = (componentName: UserProductComponentType) =>
    !!user?.productComponents?.includes(componentName);

  const hasPrime = !!user?.prime?.status && userHasPrime(user?.prime?.status);

  const hasOnlyPaidPrime =
    !!user?.prime?.status && userHasOnlyPaidPrime(user?.prime?.status);

  const hasFreeTrialPrime = user?.prime?.status === 2;
  const hasExpiredPrime = user?.prime?.status === -1;

  const userPlanCode = useMemo(() => {
    if (user?.prime?.product !== 'MD') {
      return '';
    }

    const isPrimeRelevant = hasFreeTrialPrime || hasExpiredPrime;

    if (isPrimeRelevant && user?.productCode?.includes('BasSci')) {
      return '_basicscience';
    }

    if (isPrimeRelevant && user?.productCode?.includes('Suite')) {
      return '_suite';
    }

    return '';
  }, [user]);

  const getPlansUrl = useCallback(
    (query?: string) => {
      const product = user?.prime?.product?.toLowerCase();

      return buildPlansUrl(product, query);
    },
    [user],
  );
  return (
    <UserContext.Provider
      value={{
        user,
        // entitlements,
        hasPrime,
        hasOnlyPaidPrime,
        hasExpiredPrime,
        hasFreeTrialPrime,
        userPlanCode,
        hasProductComponent,
        isUserLoaded,
        setUser: handleSetUser,
        unsetUser: handleUnsetUser,
        getPlansUrl,
        hasEolsToken,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useUserContext = (): UserContextProps => useContext(UserContext);
